
import { Direction, NormalizedEvent, Party } from "./types";

const isEnglish = (lang?: string): boolean =>
  !!lang && lang.toString().toLowerCase().startsWith("en");

const collapseDoubledSentence = (text: string): string => {
  const t = (text || "").trim();
  if (!t) return text;
  const words = t.split(/\s+/);
  const n = words.length;
  if (n < 4 || n % 2 !== 0) return text;
  const half = n / 2;
  const norm = (w: string): string =>
    w.toLowerCase().replace(/[\s.,!?¿¡;:'"“”‘’()\[\]{}\-–—_/\\]+/g, "");
  for (let i = 0; i < half; i++) {
    if (norm(words[i]) !== norm(words[i + half])) return text;
  }
  if (!/[.!?]$/.test(words[half - 1])) return text;
  return words.slice(0, half).join(" ");
};

const decode = (v: any): string => {
  if (v == null) return "";
  try {
    return decodeURIComponent(v.toString());
  } catch {
    return v.toString();
  }
};

export function classifyParty(data: Record<string, any>): Party {
  if (data?.source === "type-to-talk") return "agent";
  const role = (data?.role ?? "").toString().toLowerCase();
  if (role === "agent" || role === "caller" || role === "interpreter") return role as Party;

  const identity = decode(data?.identity);
  if (/interpreter/i.test(identity)) return "interpreter";
  if (identity === "Agent") return "agent";
  if (identity === "Caller") return "caller";

  const speaker = (data?.speaker ?? "").toString().toLowerCase();
  if (speaker === "interpreter" || speaker === "terp") return "interpreter";
  if (speaker === "agent") return "agent";
  if (speaker === "caller" || speaker === "recipient") return "caller";

  return data?.direction === "outbound" ? "agent" : "caller";
}

export function normalizeStable(data: Record<string, any>): NormalizedEvent | null {
  if (!data || data.status === "suppressed") return null;

  const party = classifyParty(data);
  const rawId = (data.id ?? "").toString();
  const isLive = rawId.startsWith("live:");
  const isFinal = !!data.isFinal || data.type === "segment.finalized";

  const translations = data.translations || {};
  const languages = data.languages || {};
  const sourceLang = (languages.from || translations.language || data.language || "")
    .toString()
    .toLowerCase();
  const targetLang = (languages.to || "").toString().toLowerCase();

  const text = (data.transcription ?? data.text ?? "").toString();

  
  const from = (translations.from ?? "").toString();
  const to = (translations.to ?? "").toString();
  const hasTranslation = !!from && !!to && from.trim() !== to.trim();

  let original: string;
  let english = "";
  if (hasTranslation) {
    if (isEnglish(sourceLang)) {
      original = from;
      english = from;
    } else {
      original = from;
      english = to;
    }
  } else {
    original = text;
    english = isEnglish(sourceLang) ? text : "";
  }

  if (isFinal) {
    original = collapseDoubledSentence(original);
    english = collapseDoubledSentence(english);
  }

  return {
    kind: "transcript",
    callId: data.conversationId || data.callId || data.conversation_id,
    party,
    direction: (data.direction as Direction) || undefined,
    rawId,
    segmentId: data.segmentId ? data.segmentId.toString() : undefined,
    orderSeq: typeof data.orderSeq === "number" ? data.orderSeq : undefined,
    turnId: data.utteranceId ? data.utteranceId.toString() : undefined,
    isLive,
    isFinal,
    revision: typeof data.revision === "number" ? data.revision : undefined,
    displaySeq: typeof data.displaySeq === "number" ? data.displaySeq : undefined,
    original,
    english,
    sourceLang,
    targetLang,
    isTranslationUpdate: !!data.isTranslationUpdate,
    source: data.source,
    createdAt: data.createdAt,
    aiInterpreter: /^AI\s+Interpreter/i.test(decode(data.identity)),
    historical: data.historical === true
  };
}

/** Normalize an `interpretation` (delta) payload. */
export function normalizeInterpretation(data: Record<string, any>): NormalizedEvent | null {
  if (!data) return null;
  const party: Party =
    data.party === "interpreter" || data.party === "caller" || data.party === "agent"
      ? data.party
      : "caller";
  return {
    kind: "interpretation",
    callId: data.callId || data.conversationId || data.conversation_id,
    party,
    segmentId: data.segmentId ? data.segmentId.toString() : undefined,
    orderSeq: typeof data.orderSeq === "number" ? data.orderSeq : undefined,
    turnId: data.turnId ? data.turnId.toString() : undefined,
    seq: typeof data.seq === "number" ? data.seq : undefined,
    chunk: (data.chunk ?? "").toString(),
    done: !!data.done,
    utteranceEnd: !!data.utteranceEnd
  };
}

/** Normalize an optimistic Type-to-Talk injection into a final agent utterance. */
export function normalizeTypeToTalk(
  englishText: string,
  foreignText?: string
): NormalizedEvent {
  const english = (englishText || "").trim();
  const foreign = (foreignText || "").trim();
  return {
    kind: "transcript",
    party: "agent",
    direction: "outbound",
    rawId: `ttt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    isFinal: true,
    revision: 1,
    original: foreign || english,
    english,
    sourceLang: foreign ? "und" : "en",
    targetLang: "en",
    source: "type-to-talk",
    optimistic: true,
    createdAt: Date.now()
  };
}

/**
 * Normalize a persisted history message row into a final historical transcript
 * event. `row` is the message entity returned by the messages API.
 */
export function normalizeHistoryMessage(row: Record<string, any>): NormalizedEvent | null {
  if (!row) return null;
  let meta: any = row.metadata;
  try {
    meta = typeof meta === "string" ? JSON.parse(meta) : meta;
  } catch {
    meta = {};
  }
  const direction: Direction = row.direction === "outbound" ? "outbound" : "inbound";
  const identity = decode(meta?.identity);
  let party: Party;
  if (/interpreter/i.test(identity)) party = "interpreter";
  else if (identity === "Agent" || direction === "outbound") party = "agent";
  else party = "caller";

  const aiInterpreter = /^AI\s+Interpreter/i.test(identity);

  const displaySeq = Number(meta?.displaySeq ?? meta?.seq) || 0;
  const timelineMs = Number(meta?.timelineMs) || 0;
  const createdMs = (() => {
    const ms = new Date(meta?.createdAt || row.created_at).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  })();
  const flushClock = displaySeq > 1e12 ? displaySeq : timelineMs > 1e12 ? timelineMs : 0;
  const historyOrder = flushClock > 0 ? flushClock : createdMs + 1e15;

  const lang = (row.language || "").toString().toLowerCase();
  const translated =
    !!row.body_transl && !!row.language && lang !== "en" && row.body !== row.body_transl;
  const original = collapseDoubledSentence((row.body ?? row.message_body ?? "").toString());
  const english = collapseDoubledSentence(
    translated
      ? (row.body_transl || row.message_body || "").toString()
      : isEnglish(lang) || !lang
      ? original
      : ""
  );

  return {
    kind: "transcript",
    callId: row.conversation_id,
    party,
    direction,
    rawId: (row.message_id ?? "").toString(),
    isFinal: true,
    revision: 1,
    original,
    english,
    sourceLang: lang,
    targetLang: translated ? "en" : lang,
    createdAt: meta?.createdAt || row.created_at,
    aiInterpreter,
    historical: true,
    historyOrder
  };
}
